import type { SimEvents } from '../sim/events';
import { WEAPON_BY_ID } from '../sim/weapons/defs';
import { playSfx, type SfxId } from './synth';
import { createMusicLoop } from './music';

export type Mixer = {
  sfx: number;
  music: number;
  muted: boolean;
  /**
   * Builds the AudioContext without starting it. Construction opens the output device and costs
   * ~200 ms of main thread, so it belongs at boot (behind the title screen), not on the first
   * frame of a match where it read as a stutter in the countdown.
   */
  warm: () => void;
  resume: () => void;
  handle: (events: SimEvents) => void;
  startMusic: () => void;
  stopMusic: () => void;
  setTension: (value: number) => void;
  dispose: () => void;
};

export function createMixer(): Mixer {
  let ctx: AudioContext | null = null;
  let sfxGain: GainNode | null = null;
  let musicGain: GainNode | null = null;
  let musicLoop: ReturnType<typeof createMusicLoop> | null = null;
  let disposed = false;
  let tension = 0;
  const mixer: Mixer = {
    sfx: 0.8,
    music: 0.25,
    muted: false,
    warm() {
      if (disposed || ctx || typeof AudioContext === 'undefined') return;
      ctx = new AudioContext();
      sfxGain = ctx.createGain();
      musicGain = ctx.createGain();
      sfxGain.connect(ctx.destination);
      musicGain.connect(ctx.destination);
      musicLoop = createMusicLoop(ctx, musicGain);
      musicLoop.setTension(tension);
      apply();
    },
    resume() {
      mixer.warm();
      if (!ctx) return;
      if (ctx.state === 'suspended') void ctx.resume().catch(() => undefined);
      apply();
    },
    handle(events) {
      if (!ctx || !sfxGain || mixer.muted) return;
      if (events.some((event) => event.type === 'hazard-warning')) playSfx(ctx, sfxGain, 'hazard.warning');
      for (const ev of events) {
        if (ev.type === 'shot') {
          const sound = WEAPON_BY_ID.get(ev.weaponId)?.def.sound;
          playSfx(ctx, sfxGain, (sound as SfxId | undefined) ?? 'shot.small');
        }
        if (ev.type === 'hit') playSfx(ctx, sfxGain, 'hit');
        if (ev.type === 'punch') playSfx(ctx, sfxGain, 'punch');
        if (ev.type === 'block') playSfx(ctx, sfxGain, ev.reflected ? 'block.perfect' : 'block');
        if (ev.type === 'clash') playSfx(ctx, sfxGain, 'clash');
        if (ev.type === 'explosion') playSfx(ctx, sfxGain, 'explosion');
        if (ev.type === 'cannon-shot') playSfx(ctx, sfxGain, 'shot.heavy');
        if (ev.type === 'siphon') playSfx(ctx, sfxGain, 'pickup');
        if (ev.type === 'pickup') playSfx(ctx, sfxGain, 'pickup');
        if (ev.type === 'kill') playSfx(ctx, sfxGain, 'death');
      }
    },
    startMusic() {
      if (disposed) return;
      mixer.resume();
      musicLoop?.start();
    },
    stopMusic() {
      musicLoop?.stop();
    },
    setTension(value) {
      tension = value;
      musicLoop?.setTension(value);
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      musicLoop?.stop();
      musicLoop = null;
      sfxGain?.disconnect();
      musicGain?.disconnect();
      if (ctx && ctx.state !== 'closed') void ctx.close().catch(() => undefined);
      ctx = null;
      sfxGain = musicGain = null;
    },
  };
  function apply() {
    const time = ctx?.currentTime ?? 0;
    if (sfxGain) sfxGain.gain.setTargetAtTime(mixer.muted ? 0 : mixer.sfx, time, 0.01);
    if (musicGain) musicGain.gain.setTargetAtTime(mixer.muted ? 0 : mixer.music, time, 0.01);
  }
  return mixer;
}
